// API + WS base URLs are resolved at runtime from window.location.
// The backend serves both the React bundle AND every /licenses, /users,
// /api/email, /worker/* etc. route — so whichever host loaded the page
// is also where API calls must go. Hardcoding a single env's hostname
// (predeployment / app / localhost) caused reset-password links opened
// on app.prigmar.com to call predeployment.prigmar.com → CORS / wrong
// DB → "You have no permission to register".
//
// SSR / build-time safety: window may be undefined when the bundle is
// imported in a Jest test or by SSR tooling. Default to predeployment
// in that case — never reaches the browser.
const _runtimeOrigin = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : 'https://predeployment.prigmar.com';
const _runtimeWsOrigin = _runtimeOrigin.replace(/^http/, 'ws');

export const url = _runtimeOrigin;
export const urlWS = _runtimeWsOrigin;

// Per-origin keys for localStorage — so two different envs in the same
// browser don't stomp each other's tokens.
const _hostSlug = (_runtimeOrigin.split('//')[1] || 'env').split('.')[0] || 'env';
export const tokenRef             = `${_hostSlug}_token`;
export const user_permissionsRef  = `${_hostSlug}_user_permissions`;
export const company_permissionsRef = `${_hostSlug}_company_permissions`;
export const all_permissionsRef   = `${_hostSlug}_all_permissions`;

export const defaultOptions = {
  headers: {
    "Content-Type": "application/json",
    "User-Agent": "prigmar-Web-Deployment",

    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Methods": "*",

    Authorization: null,
  },
};

export const compressedOptions = {
  headers: {
    "Content-Type": "application/gzip",
    "content-encoding": "gzip",
    "User-Agent": "prigmar-Web-Deployment",

    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Methods": "*",

    Authorization: null,
  },
};

export function setJWT(token) {
  defaultOptions.headers.Authorization = "Bearer " + token;
  compressedOptions.headers.Authorization = "Bearer " + token;
}

// Parse a response body as JSON, but turn a 502/503/504 or any HTML error page
// (e.g. nginx while a backend restarts/deploys) into a typed "serverUnavailable"
// error instead of an opaque "Unexpected token '<', \"<!DOCTYPE\"..." JSON-parse
// crash. Callers can check `err.serverUnavailable` to show a reconnecting state.
export async function parseJsonSafe(res) {
  const text = await res.text();
  if (/^\s*</.test(text) || [502, 503, 504].includes(res.status)) {
    const e = new Error("Service temporarily unavailable — reconnecting…");
    e.serverUnavailable = true;
    e.status = res.status;
    throw e;
  }
  try {
    return JSON.parse(text);
  } catch (_) {
    const e = new Error("Unexpected server response");
    e.serverUnavailable = true;
    e.status = res.status;
    throw e;
  }
}

export function post(endpoint, body, formData = false) {
  // console.log(defaultOptions.Authorization);
  // setJWT(localStorage.prigmar_token)
  const baseUrl =  url;
  const formedUrl = baseUrl;
  return fetch(`${formedUrl}${endpoint}`, {
    method: "POST",
    body: formData ? body : JSON.stringify(body),
    headers: formData
      ? {
          Authorization: defaultOptions.headers.Authorization,
        }
      : defaultOptions.headers,
  }).then(async (res) => {
    const data = await parseJsonSafe(res);
    if (!res.ok) throw data;
    return data;
  });
}

export function globalPut(endpoint, body, formData = false) {
  const baseUrl =  url;
  return fetch(`${baseUrl}${endpoint}`, {
    method: "PUT",
    headers: formData
      ? {
          Authorization: defaultOptions.headers.Authorization,
        }
      : defaultOptions.headers,
    body: formData ? body : JSON.stringify(body),
  }).then(async (res) => {
    if (!res.ok) {
      throw await res.json();
    }
    return await res.json();
  });
}

export function get(endpoint) {
  const baseUrl =  url;
  return fetch(`${baseUrl}${endpoint}`, {
    method: "GET",
    mode: "cors",
    headers: endpoint.endsWith("json.gz") ? undefined : defaultOptions.headers,
  }).then((res) => parseJsonSafe(res));
}

// FETCH A FILE, NOT JSON.
//
// Documents (an invoice PDF, a reminder attachment) are served as raw bytes
// behind the same JWT as everything else — which is exactly why they cannot be
// a plain <a href>: a link carries no Authorization header, and putting the
// token in the query string would leak it into history, logs and Referers. So
// the bytes are fetched here with the header, and the caller turns them into a
// blob: URL that lives only in this tab.
//
// The error path still parses JSON, because a refusal from these routes is a
// normal `{ error }` body and the caller wants the sentence, not "failed".
export async function getBlob(endpoint) {
  const res = await fetch(`${url}${endpoint}`, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: defaultOptions.headers.Authorization },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && body.error) message = body.error;
    } catch (_) { /* not JSON — keep the status line */ }
    const e = new Error(message);
    e.status = res.status;
    throw e;
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return {
    blob,
    contentType: res.headers.get("content-type") || blob.type || "",
    filename: match ? decodeURIComponent(match[1]) : "",
  };
}

export function getWithoutURL(endpoint) {
  return fetch(`${endpoint}`, {
    method: "GET",
    mode: "cors",
    headers: endpoint.endsWith("json.gz") ? undefined : defaultOptions.headers,
  }).then(async (res) => {
    return await res.json();
  });
}
export function getCompressed(endpoint) {
  const baseUrl =  url;
  return fetch(`${baseUrl}${endpoint}`, {
    method: "GET",
    mode: "cors",
    headers: endpoint.endsWith("json.gz")
      ? undefined
      : compressedOptions.headers,
  }).then(async (res) => {
    return await res.json();
  });
}

export function remove(endpoint, object) {
  const baseUrl = url;
  return fetch(`${baseUrl}${endpoint}`, {
    method: "DELETE",
    headers: defaultOptions.headers,
    body: JSON.stringify(object),
  }).then((res) => parseJsonSafe(res));
}

export function putGeneral(endpoint, object) {
  return fetch(`${url}${endpoint}`, {
    method: "PUT",
    headers: defaultOptions.headers,
    body: JSON.stringify(object),
  }).then((res) => parseJsonSafe(res));
}

export function put(id, body, formData) {
  return fetch(`${url}/posts?id=${id}`, {
    method: "PUT",
    headers: formData
      ? {
          Authorization: defaultOptions.headers.Authorization,
        }
      : defaultOptions.headers,
    body: formData ? body : JSON.stringify(body),
  }).then(async (res) => {
    return await res.json();
  });
}

export function putBrand(id, body, formData) {
  return fetch(`${url}/brands?_id=${id}`, {
    //?_id=${id}
    method: "PUT",
    headers: !formData
      ? {
          Authorization: defaultOptions.headers.Authorization,
        }
      : defaultOptions.headers,
    body: !formData ? body : JSON.stringify(body),
  }).then(async (res) => {
    return await res.json();
  });
}

export function putUser(id, body, formData) {
  return fetch(`${url}/users`, {
    method: "PUT",
    body: JSON.stringify(body),
    headers: defaultOptions.headers,
  }).then(async (res) => {
    return await res.json();
  });
}

export function putMilestone(body) {
  return fetch(`${url}/milestones`, {
    method: "PUT",
    body: JSON.stringify(body),
    headers: defaultOptions.headers,
  }).then(async (res) => {
    return await res.json();
  });
}

export function removeBulkUsers(endpoint, object) {
  return fetch(`${url}${endpoint}`, {
    method: "DELETE",
    headers: defaultOptions.headers,
    body: JSON.stringify(object),
  }).then(async (res) => {
    console.log("res--------", res);
    return await res;
  });
}

export function removeBulkMile(endpoint, object) {
  return fetch(`${url}${endpoint}`, {
    method: "DELETE",
    headers: defaultOptions.headers,
    body: JSON.stringify(object),
  }).then(async (res) => {
    return await res.json();
  });
}

export function postpro(endpoint, body, formData = false) {
  // console.log(defaultOptions.Authorization);
  // setJWT(localStorage.prigmar_token)
  const baseUrl = url;
  return fetch(`${baseUrl}${endpoint}`, {
    method: "POST",
    body: formData ? body : JSON.stringify(body),
    headers: formData
      ? {
          Authorization: defaultOptions.headers.Authorization,
        }
      : defaultOptions.headers,
  }).then(async (res) => {
    if (!res.ok) {
      throw await res.json();
    }
    return await res.json();
  });
}

export function postCustomUrl(endpoint, body, formData = false) {
  return fetch(`${endpoint}`, {
    method: "PUT",
    timeout: 'infinte',
    body: formData ? body : JSON.stringify(body),
  }).then(async (res) => {
    if (!res.ok) {
      throw await res
    }
    return await res
  });
}
